import React, { useMemo, useRef, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactECharts from 'echarts-for-react';
import { graphic as eGraphic } from 'echarts';
import { detectTimeDivisor, getSafeTimestamp } from './visualizeData.constants';

const SIGNAL_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28',
  '#FF8042', '#0088FE', '#00C49F', '#FF6B6B', '#4ECDC4', '#45B7D1',
];

const PLAYBACK_MARK_LINE = {
  animation: false,
  silent: true,
  symbol: ['none', 'triangle'],
  symbolSize: [10, 8],
  symbolRotate: 180,
  label: {
    show: true,
    position: 'start',
    formatter: ({ value }) => `▶ ${Number(value).toFixed(2)}s`,
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 10,
    backgroundColor: '#e53935',
    borderRadius: 3,
    padding: [2, 6],
  },
  lineStyle: {
    color: '#e53935',
    width: 2,
    type: 'solid',
  },
};

const DEFAULT_WINDOW_SEC = 5;
const SLIDER_BOTTOM = 10;
const SLIDER_HEIGHT = 28;

const getDisplaySignalName = (signal) => (
  signal.signalName || signal.originalSignalName || ''
);

const OverlayChart = ({ 
  signals, 
  playbackTime = null, 
  onSeekToTime = null, 
  globalStartTs, 
  globalEndTs 
}) => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const chartRef = useRef(null);
  const [zoomRange, setZoomRange] = useState({ start: 0, end: null });

  const duration = useMemo(() => {
    if (globalStartTs == null || globalEndTs == null) return 0;
    return (globalEndTs - globalStartTs) / 1e9;
  }, [globalStartTs, globalEndTs]);

  const hasSignals = signals && signals.length > 0;

  const { baseSeries, yAxes, legendData } = useMemo(() => {
    if (!hasSignals) return { baseSeries: [], yAxes: [], legendData: [] };

    const yAxesConfig = [
      {
        type: 'value',
        position: 'left',
        splitLine: { lineStyle: { type: 'dashed', color: isDark ? '#333' : '#e0e0e0' } },
        axisLabel: { fontSize: 12, color: isDark ? '#aaa' : '#333' },
      }
    ];

    const seriesData = [];
    const legendNames = new Set();

    signals.forEach((signal, index) => {
      const data = signal.chartData || [];
      const displayName = getDisplaySignalName(signal);
      
      let divisor = 1;
      if (data.length > 0) {
        divisor = detectTimeDivisor(getSafeTimestamp(data[0].timestamp));
      }
      const startTime = globalStartTs / (1e9 / divisor);

      const mappedData = data.map(point => {
        const ts = getSafeTimestamp(point.timestamp);
        const relativeTime = (ts - startTime) / divisor;
        return [relativeTime, point.value_num, point.value_str, displayName];
      }).filter(p => p[1] != null && !isNaN(p[1]) && !isNaN(p[0]));

      legendNames.add(displayName);

      const isDiscrete = mappedData.length > 0 &&
        new Set(mappedData.map(p => p[1])).size <= 10;

      seriesData.push({
        name: displayName,
        type: 'line',
        data: mappedData,
        yAxisIndex: 0,
        showSymbol: mappedData.length <= 200,
        symbol: 'diamond',
        symbolSize: 4,
        step: isDiscrete ? 'start' : false,
        smooth: false,
        large: mappedData.length > 2000,
        sampling: isDiscrete ? undefined : 'lttb',
        lineStyle: {
          width: 1.5,
          color: SIGNAL_COLORS[index % SIGNAL_COLORS.length]
        },
        itemStyle: {
          color: SIGNAL_COLORS[index % SIGNAL_COLORS.length]
        },
      });
    });

    return { baseSeries: seriesData, yAxes: yAxesConfig, legendData: Array.from(legendNames) };
  }, [signals, globalStartTs, hasSignals, isDark]);

  // Lightweight memo: only updates when playbackTime changes, avoids
  // rebuilding all chart data on every animation frame.
  const series = useMemo(() => {
    if (baseSeries.length === 0) return baseSeries;

    return baseSeries.map((s, i) => {
      if (i !== 0) return s;
      return {
        ...s,
        markLine: {
          ...PLAYBACK_MARK_LINE,
          data: playbackTime != null ? [{ xAxis: playbackTime }] : [],
        },
      };
    });
  }, [baseSeries, playbackTime]);

  const defaultWindowPct = useMemo(() => {
    if (duration <= 0) return 100;
    return Math.min((DEFAULT_WINDOW_SEC / duration) * 100, 100);
  }, [duration]);

  const resolvedEnd = zoomRange.end ?? defaultWindowPct;

  const zoomRangeRef = useRef(zoomRange);
  zoomRangeRef.current = zoomRange;

  const sliderLineRef = useRef(null);

  useEffect(() => {
    const instance = chartRef.current?.getEchartsInstance();

    // --- auto-follow: shift zoom window when cursor leaves it ---
    if (instance && playbackTime != null && duration > 0) {
      const cursorPct = (playbackTime / duration) * 100;
      const prev = zoomRangeRef.current;
      const end = prev.end ?? defaultWindowPct;

      if (cursorPct >= end || cursorPct < prev.start) {
        const span = end - prev.start;
        const newStart = Math.max(0, Math.min(cursorPct, 100 - span));
        setZoomRange({ start: newStart, end: newStart + span });
      }
    }

    // --- slider marker: draw directly on zrender to avoid triggering datazoom events ---
    const zr = instance?.getZr();
    if (sliderLineRef.current && zr) {
      zr.remove(sliderLineRef.current);
      sliderLineRef.current = null;
    }

    if (!instance || playbackTime == null || duration <= 0) return;

    const gridRect = instance.getModel()?.getComponent('grid', 0)?.coordinateSystem?.getRect();
    if (!gridRect) return;

    const pct = playbackTime / duration;
    const x = gridRect.x + pct * gridRect.width;
    const containerHeight = instance.getHeight();
    const sliderTop = containerHeight - SLIDER_BOTTOM - SLIDER_HEIGHT;

    const line = new eGraphic.Line({
      shape: { x1: x, y1: sliderTop, x2: x, y2: sliderTop + SLIDER_HEIGHT },
      style: { stroke: '#e53935', lineWidth: 2 },
      silent: true,
      z: 100,
    });
    zr.add(line);
    sliderLineRef.current = line;
  }, [playbackTime, duration, defaultWindowPct]);

  const handleDataZoom = useCallback((params) => {
    const batch = params.batch?.[0] ?? params;
    if (batch.start != null && batch.end != null) {
      setZoomRange(prev => {
        const prevEnd = prev.end ?? defaultWindowPct;
        if (Math.abs(prev.start - batch.start) < 0.05 &&
            Math.abs(prevEnd - batch.end) < 0.05) {
          return prev;
        }
        return { start: batch.start, end: batch.end };
      });
    }
  }, [defaultWindowPct]);

  const option = useMemo(() => {
    return {
      grid: { top: 40, right: 30, bottom: 80, left: 50, containLabel: true },
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: 'none',
            title: {
              zoom: 'Box Zoom',
              back: 'Reset Zoom'
            }
          },
          restore: { title: 'Reset' },
        },
        iconStyle: {
          borderColor: '#1976d2'
        },
        right: 20,
        top: 0
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: isDark ? '#1e1e1e' : '#fff',
        borderColor: isDark ? '#444' : '#ccc',
        textStyle: { color: isDark ? '#e0e0e0' : '#333' },
        formatter: (params) => {
          if (!params || !params.length) return '';
          
          let time = params[0].value[0];
          const visibleSec = duration * (resolvedEnd / 100);
          let timeStr = time.toFixed(visibleSec <= 5 ? 3 : 2);
          
          const tipBorder = isDark ? '#444' : '#eee';
          const tipTextMain = isDark ? '#e0e0e0' : '#333';
          const tipTextSec = isDark ? '#aaa' : '#666';
          const tipLabel = isDark ? '#bbb' : '#555';

          let tooltipHtml = `<div style="font-weight:600;margin-bottom:8px;padding-bottom:4px;border-bottom:1px solid ${tipBorder};">Time: ${timeStr}s</div>`;
          
          params.forEach(param => {
            const valNum = param.value[1];
            const valStr = param.value[2];
            const label = param.value[3] || param.seriesName;
            
            let displayNum = valNum;
            if (valNum != null) {
              if (Math.abs(valNum) >= 1000 || (Math.abs(valNum) < 0.01 && valNum !== 0)) {
                displayNum = valNum.toExponential(4);
              } else {
                displayNum = valNum.toFixed(4);
              }
            } else {
              displayNum = '-';
            }
            
            let displayVal = displayNum;
            if (valStr !== undefined && valStr !== null && valStr !== '' && String(valStr) !== String(valNum)) {
              displayVal = `<span style="font-weight:600;color:${tipTextMain};">${valStr}</span> <span style="font-size:0.9em;color:${tipTextSec};">(${displayNum})</span>`;
            } else {
              displayVal = `<span style="font-weight:600;color:${tipTextMain};">${displayNum}</span>`;
            }

            tooltipHtml += `
              <div style="display:flex;align-items:center;margin-bottom:4px;">
                <span style="display:inline-block;margin-right:8px;border-radius:50%;width:10px;height:10px;background-color:${param.color};"></span>
                <span style="margin-right:12px;color:${tipLabel};">${label}:</span>
                <span style="margin-left:auto;">${displayVal}</span>
              </div>
            `;
          });
          
          return tooltipHtml;
        }
      },
      legend: {
        data: legendData,
        top: 0,
        type: 'scroll',
        textStyle: { color: isDark ? '#ccc' : '#333' },
      },
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: [0],
          start: zoomRange.start,
          end: resolvedEnd,
          bottom: SLIDER_BOTTOM,
          height: SLIDER_HEIGHT,
          borderColor: isDark ? '#444' : '#ddd',
          fillerColor: 'rgba(25,118,210,0.12)',
          handleStyle: { color: '#1976d2' },
          dataBackground: {
            lineStyle: { color: isDark ? '#555' : '#ccc' },
            areaStyle: { color: isDark ? '#2a2a2a' : '#f0f0f0' },
          },
          selectedDataBackground: {
            lineStyle: { color: '#1976d2' },
            areaStyle: { color: 'rgba(25,118,210,0.08)' },
          },
          labelFormatter: (val) => `${val.toFixed(1)}s`,
          filterMode: 'none',
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          filterMode: 'none',
        }
      ],
      xAxis: {
        type: 'value',
        name: 'Time (s)',
        nameLocation: 'middle',
        nameGap: 25,
        nameTextStyle: { color: isDark ? '#aaa' : '#333' },
        min: 0,
        max: 'dataMax',
        splitNumber: 8,
        axisLabel: {
          color: isDark ? '#aaa' : '#333',
          formatter: (val) => {
            const windowSec = duration * ((resolvedEnd - zoomRange.start) / 100);
            if (windowSec <= 1) return `${val.toFixed(3)}s`;
            if (windowSec <= 10) return `${val.toFixed(2)}s`;
            if (windowSec <= 60) return `${val.toFixed(1)}s`;
            return `${val.toFixed(0)}s`;
          }
        },
        splitLine: { lineStyle: { color: isDark ? '#333' : '#e0e0e0' } },
      },
      yAxis: yAxes,
      series: series,
      animation: false
    };
  }, [series, yAxes, legendData, duration, zoomRange, resolvedEnd, isDark]);

  const prevSignalCountRef = useRef(signals.length);
  useLayoutEffect(() => {
    if (signals.length < prevSignalCountRef.current) {
      const instance = chartRef.current?.getEchartsInstance();
      if (instance) {
        instance.setOption(option, { notMerge: true, lazyUpdate: true });
      }
    }
    prevSignalCountRef.current = signals.length;
  }, [signals.length, option]);

  const onEvents = useMemo(() => ({
    click: (params) => {
      if (onSeekToTime && params.value && params.value[0] != null) {
        onSeekToTime(params.value[0]);
      }
    },
    datazoom: handleDataZoom,
  }), [onSeekToTime, handleDataZoom]);

  useEffect(() => {
    if (chartRef.current && onSeekToTime) {
      const zr = chartRef.current.getEchartsInstance().getZr();
      const clickHandler = (params) => {
        if (!params.target) {
          const pointInPixel = [params.offsetX, params.offsetY];
          const echartInstance = chartRef.current.getEchartsInstance();
          if (echartInstance.containPixel('grid', pointInPixel)) {
            const xValue = echartInstance.convertFromPixel({ seriesIndex: 0 }, pointInPixel)[0];
            onSeekToTime(xValue);
          }
        }
      };
      zr.on('click', clickHandler);
      return () => {
        zr.off('click', clickHandler);
      };
    }
  }, [onSeekToTime]);


  return (
    <Box className="signal-chart-wrapper">
      {hasSignals ? (
        <Box className="signal-chart-inner-wrapper">
          <ReactECharts
            ref={chartRef}
            option={option}
            style={{ height: '100%', width: '100%' }}
            notMerge={false}
            lazyUpdate={true}
            onEvents={onEvents}
          />
        </Box>
      ) : (
        <Typography variant="body2" className="signal-chart-no-data">
          No signals selected
        </Typography>
      )}
    </Box>
  );
};

export default OverlayChart;